// src/components/GenerateObjectRemoverModal.jsx
import React, { useState, useRef, useEffect, useCallback, Fragment } from "react";
import axios from "axios";
import { useAuth } from "@clerk/clerk-react";
import { Dialog } from "@headlessui/react";
import { X, Brush, Eraser, Loader2, AlertCircle, Plus, Trash2 } from "lucide-react";

const BRAND_PINK = "#FC2783";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "https://ecom-ads-ai-backend-production.up.railway.app";

const steps = [
  { number: 1, label: "Select Image" },
  { number: 2, label: "Paint Mask" },
];

function StepperHeader({ step }) {
  return (
    <div className="flex items-center gap-2 px-8 pt-7 pb-2">
      {steps.map((s, idx) => (
        <Fragment key={s.number}>
          <span
            className={`rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold ${
              step === s.number ? "bg-pink-600 text-white" : "bg-[#29294a] text-gray-400"
            }`}
          >
            {s.number}
          </span>
          <span
            className={`text-base sm:text-lg font-semibold ${
              step === s.number ? "text-white" : "text-gray-400"
            } ${idx !== steps.length - 1 ? "mr-8" : ""}`}
          >
            {s.label}
          </span>
        </Fragment>
      ))}
    </div>
  );
}

function StepFooter({ step, onBack, onNext, onGenerate, canNext, canGenerate, generating }) {
  return (
    <div className="absolute bottom-0 left-0 w-full bg-[#191930] border-t border-[#23233a] flex justify-between items-center px-8 py-4 z-20">
      <button
        className="px-7 py-2 rounded-xl font-bold bg-[#222246] hover:bg-[#2B2B4A] text-white disabled:opacity-40"
        disabled={step === 1 || generating}
        onClick={onBack}
        type="button"
      >
        Back
      </button>

      {step === 1 ? (
        <button
          className="px-7 py-2 bg-pink-600 hover:bg-pink-700 rounded-xl font-bold text-white disabled:opacity-40"
          onClick={onNext}
          disabled={!canNext || generating}
          type="button"
        >
          Next
        </button>
      ) : (
        <button
          className="px-7 py-2 bg-pink-600 hover:bg-pink-700 rounded-xl font-bold text-white disabled:opacity-40 flex items-center"
          onClick={onGenerate}
          disabled={!canGenerate || generating}
          type="button"
        >
          {generating && <Loader2 className="mr-2 animate-spin" size={18} />}
          {generating ? "Generating…" : "Generate"}
        </button>
      )}
    </div>
  );
}

export default function GenerateObjectRemoverModal({
  productImages = [],
  onClose,
  onGenerate, // REQUIRED: (imageUrl, maskDataUrl) => Promise<void>
  // projectId is no longer needed here because parent handles the request
}) {
  const { getToken } = useAuth();

  const [step, setStep] = useState(1);
  const [selectedImage, setSelectedImage] = useState(null);

  // ✅ Step 1 manual upload (same flow as Ad Marketing Video modal)
  const [step1UploadedAsset, setStep1UploadedAsset] = useState(null); // { id, url } | null
  const [step1UploadLoading, setStep1UploadLoading] = useState(false);
  const step1FileInputRef = useRef(null);
  const [step1Error, setStep1Error] = useState("");

  /** Canvas / painting **/
  const canvasRef = useRef(null);
  const imgRef = useRef(null);

  const [brushSize, setBrushSize] = useState(28);
  const [eraseMode, setEraseMode] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasPaint, setHasPaint] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Natural size for mask upscaling (so Phot.AI aligns the mask perfectly)
  const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 });

  // Pink cursor ring
  const [cursor, setCursor] = useState({ x: 0, y: 0, visible: false });

  // Edge controls to kill “ghosts”
  const [expandPx, setExpandPx] = useState(4); // dilate radius in natural pixels
  const [featherPx, setFeatherPx] = useState(1); // tiny pre-threshold blur

  // Fit image to the canvas area when entering Step 2
  useEffect(() => {
    if (step !== 2 || !selectedImage) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const natW = img.naturalWidth || img.width;
      const natH = img.naturalHeight || img.height;
      setNaturalSize({ w: natW, h: natH });

      const maxW = 820,
        maxH = 520;
      let w = natW,
        h = natH;
      const r = Math.min(maxW / w, maxH / h, 1);
      w = Math.max(1, Math.round(w * r));
      h = Math.max(1, Math.round(h * r));

      canvas.width = w;
      canvas.height = h;
      ctx.clearRect(0, 0, w, h); // transparent base
      setHasPaint(false);

      if (imgRef.current) {
        imgRef.current.src = selectedImage;
        imgRef.current.width = w;
        imgRef.current.height = h;
      }
    };
    img.src = selectedImage;
  }, [step, selectedImage]);

  const handlePointer = (e, type) => {
    if (step !== 2) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const getXY = (ev) => {
      const cx = (ev.clientX ?? ev.touches?.[0]?.clientX) - rect.left;
      const cy = (ev.clientY ?? ev.touches?.[0]?.clientY) - rect.top;
      return [cx, cy];
    };
    const ctx = canvas.getContext("2d");
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.lineWidth = brushSize;

    if (type === "enter") {
      setCursor((c) => ({ ...c, visible: true }));
    } else if (type === "leave") {
      setCursor((c) => ({ ...c, visible: false }));
      setIsDrawing(false);
    } else if (type === "touchstart" || type === "touchmove") {
      setCursor((c) => ({ ...c, visible: false })); // hide ring on touch
    } else if (type === "touchend") {
      setIsDrawing(false);
    }

    if (type === "down") {
      setIsDrawing(true);
      const [x, y] = getXY(e);
      setCursor({ x, y, visible: true });
      ctx.globalCompositeOperation = eraseMode ? "destination-out" : "source-over";
      ctx.strokeStyle = eraseMode ? "rgba(0,0,0,1)" : "rgba(255,255,255,1)"; // white = REMOVE
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + 0.01, y + 0.01); // tap-dot
      ctx.stroke();
      setHasPaint(true);
    } else if (type === "move") {
      const [x, y] = getXY(e);
      setCursor({ x, y, visible: true });
      if (isDrawing) {
        ctx.globalCompositeOperation = eraseMode ? "destination-out" : "source-over";
        ctx.strokeStyle = eraseMode ? "rgba(0,0,0,1)" : "rgba(255,255,255,1)";
        ctx.lineTo(x, y);
        ctx.stroke();
        setHasPaint(true);
      }
    } else if (type === "up") {
      setIsDrawing(false);
    }
  };

  const clearMask = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height); // back to fully transparent
    setHasPaint(false);
  };

  const onBack = () => setStep((s) => Math.max(1, s - 1));
  const onNext = () => {
    if (!selectedImage) return;
    setStep(2);
  };

  /**
   * Build final mask:
   *  - Upscale to natural size
   *  - Optional tiny feather
   *  - Binarize (alpha → 0 / 255, RGB → white)
   *  - Dilate by expandPx
   */
  const buildMaskDataUrl = useCallback(() => {
    const viewCanvas = canvasRef.current;
    if (!viewCanvas || !hasPaint) return "";

    const targetW = naturalSize.w || viewCanvas.width;
    const targetH = naturalSize.h || viewCanvas.height;

    // Upscale strokes
    const up = document.createElement("canvas");
    up.width = Math.max(1, targetW);
    up.height = Math.max(1, targetH);
    const uctx = up.getContext("2d");
    uctx.clearRect(0, 0, up.width, up.height);
    uctx.imageSmoothingEnabled = true;
    uctx.drawImage(viewCanvas, 0, 0, viewCanvas.width, viewCanvas.height, 0, 0, up.width, up.height);

    const getImageData = (c) => c.getContext("2d").getImageData(0, 0, c.width, c.height);
    const putImageData = (c, id) => c.getContext("2d").putImageData(id, 0, 0);

    // Feather (very light) to soften stair-steps before thresholding
    if (featherPx > 0) {
      const tmp = document.createElement("canvas");
      tmp.width = up.width;
      tmp.height = up.height;
      const tctx = tmp.getContext("2d");
      tctx.drawImage(up, 0, 0);
      const passes = Math.min(3, featherPx);
      for (let p = 0; p < passes; p++) {
        for (let dx = -1; dx <= 1; dx++) if (dx !== 0) tctx.drawImage(tmp, dx, 0);
        for (let dy = -1; dy <= 1; dy++) if (dy !== 0) tctx.drawImage(tmp, 0, dy);
      }
      uctx.clearRect(0, 0, up.width, up.height);
      uctx.drawImage(tmp, 0, 0);
    }

    // Binarize alpha; force white where painted, transparent elsewhere
    let id = getImageData(up);
    const d = id.data;
    for (let i = 0; i < d.length; i += 4) {
      const a = d[i + 3];
      if (a > 16) {
        d[i] = 255;
        d[i + 1] = 255;
        d[i + 2] = 255;
        d[i + 3] = 255;
      } else {
        d[i] = 0;
        d[i + 1] = 0;
        d[i + 2] = 0;
        d[i + 3] = 0;
      }
    }
    putImageData(up, id);

    // Dilate (expand) by expandPx to fully cover edges/shadows
    if (expandPx > 0) {
      const src = getImageData(up);
      const out = new ImageData(up.width, up.height);
      out.data.set(src.data);
      const sw = up.width,
        sh = up.height;
      const r = Math.max(1, Math.min(12, expandPx));

      for (let y = 0; y < sh; y++) {
        for (let x = 0; x < sw; x++) {
          const idx = (y * sw + x) * 4;
          if (src.data[idx + 3] === 255) {
            const x0 = Math.max(0, x - r),
              x1 = Math.min(sw - 1, x + r);
            const y0 = Math.max(0, y - r),
              y1 = Math.min(sh - 1, y + r);
            for (let yy = y0; yy <= y1; yy++) {
              let base = (yy * sw + x0) * 4;
              for (let xx = x0; xx <= x1; xx++, base += 4) {
                out.data[base] = 255;
                out.data[base + 1] = 255;
                out.data[base + 2] = 255;
                out.data[base + 3] = 255;
              }
            }
          }
        }
      }
      putImageData(up, out);
    }

    return up.toDataURL("image/png");
  }, [hasPaint, naturalSize.w, naturalSize.h, expandPx, featherPx]);

  const handleGenerate = async () => {
    if (!selectedImage) {
      alert("Please select an image in Step 1.");
      return;
    }
    if (!hasPaint) {
      alert("Please paint the area you want to REMOVE in Step 2.");
      return;
    }

    const dataUrl = buildMaskDataUrl();
    if (!dataUrl) {
      alert("Your mask looks empty. Paint over the object you want to remove, then try again.");
      return;
    }

    if (typeof onGenerate !== "function") {
      alert("onGenerate handler is missing.");
      return;
    }

    setGenerating(true);
    try {
      // Delegate to parent: it will insert the optimistic spinner,
      // upload the mask, queue the job, and start polling.
      await onGenerate(selectedImage, dataUrl);

      // Close ONLY on success; if parent throws, we stay open.
      onClose?.();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Generate failed (modal):", e);
      alert("Generate failed. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const canNext = !!selectedImage;
  const canGenerate = !!selectedImage && hasPaint;

  // ✅ Step 1 manual upload handler (same behavior as Ad Marketing Video modal)
  const handleStep1Upload = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setStep1UploadLoading(true);
      setStep1Error("");

      try {
        if (file.size > 20 * 1024 * 1024) {
          setStep1Error("Image is too large. Please upload a file under 20MB.");
          return;
        }

        const token = await getToken();
        const formData = new FormData();
        formData.append("file", file);

        // Reuse existing upload endpoint (same as Ad Marketing Video / End card upload)
        const { data } = await axios.post(
          `${BACKEND_URL}/api/videos/marketing/endcard/upload`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );

        const url = data?.url;
        if (url) {
          const newAsset = { id: `object-remover-upload-${Date.now()}`, url };
          setStep1UploadedAsset(newAsset);
          setSelectedImage(url); // single-select behavior
        } else {
          setStep1Error("Upload succeeded but no URL was returned.");
        }
      } catch {
        setStep1Error("Failed to upload image.");
      } finally {
        setStep1UploadLoading(false);
        // allow re-uploading the same file
        // eslint-disable-next-line no-param-reassign
        event.target.value = "";
      }
    },
    [getToken]
  );

  const isUploadedSelected = !!step1UploadedAsset && selectedImage === step1UploadedAsset.url;

  // Pink cursor ring style (matches Replacer modal)
  const ringStyle = {
    position: "absolute",
    left: cursor.x,
    top: cursor.y,
    width: brushSize,
    height: brushSize,
    transform: "translate(-50%, -50%)",
    pointerEvents: "none",
    borderRadius: "9999px",
    border: `2px solid ${BRAND_PINK}`,
    backgroundColor: "rgba(252,39,131,0.08)",
    boxShadow: "0 0 0 1px rgba(252,39,131,0.35), 0 0 12px rgba(252,39,131,0.2)",
    zIndex: 5,
  };

  return (
    <Dialog open onClose={generating ? () => {} : onClose} className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/60" aria-hidden="true" />
      <div className="relative bg-[#191930] text-white rounded-2xl shadow-2xl w-full max-w-[980px] min-h-[520px] h-[86vh] flex flex-col overflow-hidden">
        {/* Close */}
        <button
          onClick={onClose}
          aria-label="Close modal"
          className="absolute top-4 right-4 bg-white text-black p-2 rounded-full hover:bg-gray-100 z-10 disabled:opacity-40"
          disabled={generating}
          type="button"
        >
          <X size={22} />
        </button>

        <StepperHeader step={step} />

        {/* Scrollable area (up until footer), same pattern as Ad Marketing Video modal */}
        <div
          id="object-remover-modal-scroll"
          className="flex-1 flex flex-col px-8 pt-2 pb-32 min-h-0 overflow-y-auto"
        >
          {/* Step 1: Select image */}
          {step === 1 && (
            <>
              <p className="mb-4 text-gray-300 flex items-center gap-4">
                Select an image to remove objects from.
                <span className="text-gray-400 text-sm">Selected {selectedImage ? 1 : 0} / 1</span>
              </p>

              {!!step1Error && <div className="mb-3 text-red-500 font-semibold">{step1Error}</div>}

              <input
                ref={step1FileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleStep1Upload}
              />

              <div className="rounded-lg border border-[#23233a] bg-[#191930] px-4 py-3">
                <div className="grid grid-cols-5 gap-4">
                  {/* ✅ Upload tile (same pattern as Ad Marketing Video modal) */}
                  <div
                    className={`relative rounded-lg overflow-hidden border-2 group transition ${
                      step1UploadedAsset
                        ? isUploadedSelected
                          ? "border-pink-500 ring-2 ring-pink-600 cursor-pointer"
                          : "border-transparent hover:border-pink-400 cursor-pointer"
                        : "border-[#3A3A4A] border-dashed hover:border-pink-400 cursor-pointer"
                    }`}
                    onClick={() => {
                      if (generating || step1UploadLoading) return;

                      if (step1UploadedAsset) {
                        // behaves like a normal single-select asset toggle
                        setSelectedImage((prev) => (prev === step1UploadedAsset.url ? null : step1UploadedAsset.url));
                        return;
                      }

                      step1FileInputRef.current?.click();
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        if (generating || step1UploadLoading) return;

                        if (step1UploadedAsset) {
                          setSelectedImage((prev) => (prev === step1UploadedAsset.url ? null : step1UploadedAsset.url));
                          return;
                        }

                        step1FileInputRef.current?.click();
                      }
                    }}
                    aria-label={step1UploadedAsset ? "Uploaded image" : "Upload image"}
                  >
                    <div className="aspect-[4/5] bg-black relative">
                      {step1UploadedAsset ? (
                        <img
                          className="object-cover w-full h-full"
                          src={step1UploadedAsset.url}
                          alt="Uploaded asset"
                          loading="lazy"
                          draggable={false}
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center bg-[#151529]">
                          <div className="w-12 h-12 rounded-full bg-[#23233a] border border-[#2B2B4A] flex items-center justify-center mb-3">
                            <Plus size={22} className="text-white" />
                          </div>
                          <div className="text-white font-semibold text-sm">Upload image</div>
                          <div className="text-gray-400 text-[11px] mt-1 px-3 text-center">JPG, PNG, WEBP</div>
                        </div>
                      )}

                      {step1UploadLoading && (
                        <div className="absolute inset-0 bg-black/55 flex items-center justify-center">
                          <Loader2 className="animate-spin text-white" />
                        </div>
                      )}

                      <div className="absolute inset-0 bg-black/10 group-hover:bg-pink-600/10 transition" />
                    </div>

                    {/* Actions (only when uploaded) */}
                    {step1UploadedAsset && (
                      <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition">
                        <button
                          type="button"
                          className="px-2.5 py-1.5 rounded-md bg-black/70 hover:bg-black/85 text-white text-xs font-semibold border border-white/10"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (generating || step1UploadLoading) return;
                            step1FileInputRef.current?.click();
                          }}
                          aria-label="Replace uploaded image"
                          disabled={generating || step1UploadLoading}
                        >
                          Replace
                        </button>

                        <button
                          type="button"
                          className="p-2 rounded-md bg-black/70 hover:bg-black/85 text-white border border-white/10"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (generating) return;

                            const uploadedUrl = step1UploadedAsset.url;
                            setStep1UploadedAsset(null);
                            setSelectedImage((prev) => (prev === uploadedUrl ? null : prev));
                            setStep1Error("");
                          }}
                          aria-label="Remove uploaded image"
                          disabled={generating}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Existing analyzed product images */}
                  {productImages.map((img, i) => {
                    const isSelected = selectedImage === img;
                    return (
                      <button
                        key={`${img}-${i}`}
                        type="button"
                        className={`relative rounded-lg overflow-hidden border-2 cursor-pointer group transition ${
                          isSelected
                            ? "border-pink-500 ring-2 ring-pink-600"
                            : "border-transparent hover:border-pink-400"
                        }`}
                        onClick={() => setSelectedImage(img)}
                        aria-label="Select image"
                        title="Select image"
                        disabled={generating}
                      >
                        <div className="aspect-[4/5] bg-black">
                          <img
                            src={img}
                            alt="Product"
                            className="object-cover w-full h-full"
                            loading="lazy"
                            draggable={false}
                          />
                        </div>
                        <div className="absolute inset-0 bg-black/10 group-hover:bg-pink-600/10 transition" />
                      </button>
                    );
                  })}
                </div>

                {productImages.length === 0 && !step1UploadedAsset ? (
                  <div className="text-gray-400 text-center pt-6 pb-2">
                    No analyzed product images found. You can still upload one manually.
                  </div>
                ) : null}
              </div>
            </>
          )}

          {/* Step 2: Paint mask */}
          {step === 2 && (
            <div className="grid grid-cols-1 lg:grid-cols-[auto_280px] gap-6">
              {/* Preview + Canvas */}
              <div className="relative w-full">
                <h2 className="text-xl font-bold text-white mb-3">Paint Mask</h2>
                <div
                  className={`relative inline-block rounded-xl overflow-hidden ${
                    cursor.visible ? "cursor-none" : "cursor-crosshair"
                  }`}
                  style={{ background: "#0f0f22" }}
                >
                  {/* Image beneath */}
                  <img ref={imgRef} alt="Preview" />
                  {/* Canvas on top (white = REMOVE, transparent = keep) */}
                  <canvas
                    ref={canvasRef}
                    className="absolute top-0 left-0"
                    onMouseEnter={(e) => handlePointer(e, "enter")}
                    onMouseLeave={(e) => handlePointer(e, "leave")}
                    onMouseDown={(e) => handlePointer(e, "down")}
                    onMouseMove={(e) => handlePointer(e, "move")}
                    onMouseUp={(e) => handlePointer(e, "up")}
                    onTouchStart={(e) => handlePointer(e, "touchstart")}
                    onTouchMove={(e) => handlePointer(e, "touchmove")}
                    onTouchEnd={(e) => handlePointer(e, "touchend")}
                  />
                  {/* Pink brush cursor ring */}
                  {cursor.visible && <div style={ringStyle} />}
                </div>
                <p className="text-sm text-gray-400 mt-2">
                  Paint <b>white</b> over the object you want to <b>remove</b>. Use Eraser to undo (transparent
                  elsewhere).
                </p>
              </div>

              {/* Tools */}
              <div className="bg-[#1C1C2E] border border-[#2A2A40] rounded-2xl p-4 h-fit">
                <p className="font-semibold mb-3">Brush</p>
                <div className="flex items-center gap-3 mb-4">
                  <button
                    type="button"
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${
                      !eraseMode ? "border-pink-500 bg-[#2A2A40]" : "border-transparent bg-[#1F1F2F]"
                    }`}
                    onClick={() => setEraseMode(false)}
                  >
                    <Brush size={18} /> Paint
                  </button>
                  <button
                    type="button"
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${
                      eraseMode ? "border-pink-500 bg-[#2A2A40]" : "border-transparent bg-[#1F1F2F]"
                    }`}
                    onClick={() => setEraseMode(true)}
                  >
                    <Eraser size={18} /> Erase
                  </button>
                </div>

                <label className="text-sm text-gray-300">Brush size</label>
                <input
                  type="range"
                  min={6}
                  max={80}
                  value={brushSize}
                  onChange={(e) => setBrushSize(parseInt(e.target.value, 10))}
                  className="w-full mt-2"
                />

                <label className="text-sm text-gray-300 mt-4 block">Expand mask (px)</label>
                <input
                  type="range"
                  min={0}
                  max={12}
                  value={expandPx}
                  onChange={(e) => setExpandPx(parseInt(e.target.value, 10))}
                  className="w-full mt-1"
                />
                <div className="text-xs text-gray-400 mt-1">Helps cover edges/shadows (try 3–6)</div>

                <label className="text-sm text-gray-300 mt-4 block">Feather (px)</label>
                <input
                  type="range"
                  min={0}
                  max={4}
                  value={featherPx}
                  onChange={(e) => setFeatherPx(parseInt(e.target.value, 10))}
                  className="w-full mt-1"
                />
                <div className="text-xs text-gray-400 mt-1">Softens jagged strokes (1–2)</div>

                <div className="flex gap-3 mt-4">
                  <button onClick={clearMask} type="button" className="px-4 py-2 rounded-xl bg-[#222246] hover:bg-[#2B2B4A]">
                    Clear mask
                  </button>
                </div>

                <div className="mt-3 flex items-center gap-2 text-xs text-pink-200 bg-[#221b36] border border-pink-700 rounded-lg px-2 py-2">
                  <AlertCircle size={14} className="text-pink-400" />
                  Tip: Keep edges clean; the model removes exactly the white region.
                </div>
              </div>
            </div>
          )}
        </div>

        <StepFooter
          step={step}
          onBack={onBack}
          onNext={onNext}
          onGenerate={handleGenerate}
          canNext={canNext}
          canGenerate={canGenerate}
          generating={generating}
        />
      </div>
    </Dialog>
  );
}
